/**
 * Team Topology Canvas Utilities
 * Helper functions for canvas operations
 */

import type {
  Team,
  TeamInteraction,
  Position,
  CanvasSettings,
  ConwayAlignment,
  ConwayIssue,
  TopologySnapshot,
} from './types';
import {
  TEAM_TYPE_CONFIG,
  INTERACTION_MODE_CONFIG,
  DEFAULT_TEAM_SIZE,
} from './types';

/**
 * Snap position to grid
 */
export function snapToGrid(position: Position, gridSize: number): Position {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  };
}

/**
 * Get center point of a team node
 */
export function getTeamCenter(team: Team): Position {
  return {
    x: team.position.x + DEFAULT_TEAM_SIZE.width / 2,
    y: team.position.y + DEFAULT_TEAM_SIZE.height / 2,
  };
}

/**
 * Calculate distance between two points
 */
export function distance(p1: Position, p2: Position): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Check if two team nodes overlap
 */
export function teamsOverlap(team1: Team, team2: Team, padding: number = 10): boolean {
  const rect1 = {
    left: team1.position.x - padding,
    right: team1.position.x + DEFAULT_TEAM_SIZE.width + padding,
    top: team1.position.y - padding,
    bottom: team1.position.y + DEFAULT_TEAM_SIZE.height + padding,
  };

  const rect2 = {
    left: team2.position.x,
    right: team2.position.x + DEFAULT_TEAM_SIZE.width,
    top: team2.position.y,
    bottom: team2.position.y + DEFAULT_TEAM_SIZE.height,
  };

  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
}

/**
 * Find a non-overlapping position for a new team
 */
export function findNonOverlappingPosition(
  existingTeams: Team[],
  preferredPosition: Position,
  settings: CanvasSettings
): Position {
  let position = settings.snapToGrid
    ? snapToGrid(preferredPosition, settings.gridSize)
    : preferredPosition;

  const tempTeam: Team = {
    id: 'temp',
    name: 'temp',
    type: 'stream-aligned',
    position,
  };

  let attempts = 0;
  const maxAttempts = 100;
  const offsetStep = settings.gridSize * 2;

  while (attempts < maxAttempts) {
    const overlapping = existingTeams.some(t => teamsOverlap(tempTeam, t));
    if (!overlapping) {
      return position;
    }

    // Try different positions in a spiral pattern
    const spiralIndex = Math.floor(attempts / 4);
    const direction = attempts % 4;

    switch (direction) {
      case 0:
        position = { x: preferredPosition.x + spiralIndex * offsetStep, y: preferredPosition.y };
        break;
      case 1:
        position = { x: preferredPosition.x, y: preferredPosition.y + spiralIndex * offsetStep };
        break;
      case 2:
        position = { x: preferredPosition.x - spiralIndex * offsetStep, y: preferredPosition.y };
        break;
      case 3:
        position = { x: preferredPosition.x, y: preferredPosition.y - spiralIndex * offsetStep };
        break;
    }

    if (settings.snapToGrid) {
      position = snapToGrid(position, settings.gridSize);
    }

    tempTeam.position = position;
    attempts++;
  }

  return position;
}

/**
 * Calculate the line path between two teams for an interaction
 */
export function calculateInteractionPath(
  sourceTeam: Team,
  targetTeam: Team
): { start: Position; end: Position; midpoint: Position } {
  const sourceCenter = getTeamCenter(sourceTeam);
  const targetCenter = getTeamCenter(targetTeam);

  // Calculate edge points (where line intersects team boundaries)
  const angle = Math.atan2(
    targetCenter.y - sourceCenter.y,
    targetCenter.x - sourceCenter.x
  );

  const halfWidth = DEFAULT_TEAM_SIZE.width / 2;
  const halfHeight = DEFAULT_TEAM_SIZE.height / 2;

  // Determine intersection point on source team boundary
  let startX: number, startY: number;
  const absAngle = Math.abs(angle);

  if (absAngle < Math.atan2(halfHeight, halfWidth) || absAngle > Math.PI - Math.atan2(halfHeight, halfWidth)) {
    // Intersects left or right side
    startX = sourceCenter.x + (Math.cos(angle) > 0 ? halfWidth : -halfWidth);
    startY = sourceCenter.y + (halfWidth * Math.tan(angle)) * (Math.cos(angle) > 0 ? 1 : -1);
  } else {
    // Intersects top or bottom side
    startY = sourceCenter.y + (Math.sin(angle) > 0 ? halfHeight : -halfHeight);
    startX = sourceCenter.x + (halfHeight / Math.tan(angle)) * (Math.sin(angle) > 0 ? 1 : -1);
  }

  // Determine intersection point on target team boundary
  const reverseAngle = angle + Math.PI;
  let endX: number, endY: number;
  const absReverseAngle = Math.abs(reverseAngle % Math.PI);

  if (absReverseAngle < Math.atan2(halfHeight, halfWidth) || absReverseAngle > Math.PI - Math.atan2(halfHeight, halfWidth)) {
    endX = targetCenter.x + (Math.cos(reverseAngle) > 0 ? halfWidth : -halfWidth);
    endY = targetCenter.y + (halfWidth * Math.tan(reverseAngle)) * (Math.cos(reverseAngle) > 0 ? 1 : -1);
  } else {
    endY = targetCenter.y + (Math.sin(reverseAngle) > 0 ? halfHeight : -halfHeight);
    endX = targetCenter.x + (halfHeight / Math.tan(reverseAngle)) * (Math.sin(reverseAngle) > 0 ? 1 : -1);
  }

  return {
    start: { x: startX, y: startY },
    end: { x: endX, y: endY },
    midpoint: { x: (startX + endX) / 2, y: (startY + endY) / 2 },
  };
}

/**
 * Get team color from type or custom override
 */
export function getTeamColor(team: Team): string {
  return team.color || TEAM_TYPE_CONFIG[team.type].color;
}

/**
 * Conway's Law alignment checker
 */
export function checkConwayAlignment(snapshot: TopologySnapshot): ConwayAlignment {
  const issues: ConwayIssue[] = [];
  const { teams, interactions } = snapshot;

  // Rule 1: Check for too many collaboration modes (should be temporary)
  const collaborationInteractions = interactions.filter(i => i.mode === 'collaboration');
  const collaborationRatio = collaborationInteractions.length / (interactions.length || 1);

  if (collaborationRatio > 0.5 && interactions.length > 2) {
    issues.push({
      severity: 'warning',
      message: 'High proportion of collaboration interactions',
      suggestion: 'Consider converting stable collaborations to X-as-a-Service patterns for clearer boundaries',
    });
  }

  // Rule 2: Check for teams with too many interactions
  const teamInteractionCount = new Map<string, number>();
  interactions.forEach(i => {
    teamInteractionCount.set(i.sourceTeamId, (teamInteractionCount.get(i.sourceTeamId) || 0) + 1);
    teamInteractionCount.set(i.targetTeamId, (teamInteractionCount.get(i.targetTeamId) || 0) + 1);
  });

  teams.forEach(team => {
    const count = teamInteractionCount.get(team.id) || 0;
    if (count > 5) {
      issues.push({
        severity: 'error',
        teamId: team.id,
        message: `Team "${team.name}" has ${count} interactions, which may indicate too broad a scope`,
        suggestion: 'Consider splitting this team or reducing its responsibilities',
      });
    } else if (count > 3) {
      issues.push({
        severity: 'warning',
        teamId: team.id,
        message: `Team "${team.name}" has ${count} interactions`,
        suggestion: 'Monitor cognitive load and consider if some interactions can be consolidated',
      });
    }
  });

  // Rule 3: Check for stream-aligned teams providing services (should be platform teams)
  interactions.forEach(interaction => {
    const sourceTeam = teams.find(t => t.id === interaction.sourceTeamId);
    if (
      sourceTeam?.type === 'stream-aligned' &&
      interaction.mode === 'x-as-a-service'
    ) {
      issues.push({
        severity: 'warning',
        teamId: sourceTeam.id,
        interactionId: interaction.id,
        message: `Stream-aligned team "${sourceTeam.name}" is providing a service`,
        suggestion: 'Consider if this should be a Platform team, or if the service should be extracted',
      });
    }
  });

  // Rule 4: Check for enabling teams with long-term collaborations
  interactions.forEach(interaction => {
    const sourceTeam = teams.find(t => t.id === interaction.sourceTeamId);
    if (
      sourceTeam?.type === 'enabling' &&
      interaction.mode === 'collaboration' &&
      !interaction.temporary
    ) {
      issues.push({
        severity: 'warning',
        teamId: sourceTeam.id,
        interactionId: interaction.id,
        message: `Enabling team "${sourceTeam.name}" has a non-temporary collaboration`,
        suggestion: 'Enabling teams should facilitate learning and then step back',
      });
    }
  });

  // Rule 5: Check for high cognitive load
  teams.forEach(team => {
    if (team.cognitiveLoad === 'high') {
      issues.push({
        severity: 'error',
        teamId: team.id,
        message: `Team "${team.name}" has high cognitive load`,
        suggestion: 'Consider splitting responsibilities, reducing scope, or adding platform support',
      });
    }
  });

  // Calculate score
  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const score = Math.max(0, 100 - (errorCount * 20) - (warningCount * 5));

  // Generate recommendations
  const recommendations: string[] = [];

  if (errorCount === 0 && warningCount === 0) {
    recommendations.push('Your team topology looks well-aligned with Conway\'s Law!');
  }

  if (collaborationRatio > 0.3) {
    recommendations.push('Review collaboration patterns - aim for clear X-as-a-Service boundaries where possible');
  }

  const platformTeams = teams.filter(t => t.type === 'platform').length;
  const streamAlignedTeams = teams.filter(t => t.type === 'stream-aligned').length;

  if (streamAlignedTeams > 3 && platformTeams === 0) {
    recommendations.push('Consider introducing a Platform team to reduce duplicate infrastructure work');
  }

  const enablingTeams = teams.filter(t => t.type === 'enabling').length;
  if (teams.length > 5 && enablingTeams === 0) {
    recommendations.push('An Enabling team could help with capability building across teams');
  }

  return {
    score,
    issues,
    recommendations,
  };
}

/**
 * Auto-layout teams in a grid pattern
 */
export function autoLayoutTeams(
  teams: Team[],
  settings: CanvasSettings,
  canvasWidth: number = 1200
): Team[] {
  const padding = settings.gridSize * 2;
  const teamWidth = DEFAULT_TEAM_SIZE.width + padding;
  const teamHeight = DEFAULT_TEAM_SIZE.height + padding;

  const columnsPerRow = Math.max(1, Math.floor(canvasWidth / teamWidth));

  return teams.map((team, index) => ({
    ...team,
    position: {
      x: padding + (index % columnsPerRow) * teamWidth,
      y: padding + Math.floor(index / columnsPerRow) * teamHeight,
    },
  }));
}

/**
 * Group teams by type for organized layout
 */
export function groupedAutoLayout(
  teams: Team[],
  settings: CanvasSettings
): Team[] {
  const padding = settings.gridSize * 2;
  const teamWidth = DEFAULT_TEAM_SIZE.width + padding;
  const teamHeight = DEFAULT_TEAM_SIZE.height + padding * 3;

  // Group by type
  const grouped = {
    'stream-aligned': teams.filter(t => t.type === 'stream-aligned'),
    'platform': teams.filter(t => t.type === 'platform'),
    'enabling': teams.filter(t => t.type === 'enabling'),
    'complicated-subsystem': teams.filter(t => t.type === 'complicated-subsystem'),
  };

  let yOffset = padding;
  const result: Team[] = [];

  // Layout each group
  Object.entries(grouped).forEach(([type, typeTeams]) => {
    if (typeTeams.length === 0) return;

    typeTeams.forEach((team, index) => {
      result.push({
        ...team,
        position: {
          x: padding + index * teamWidth,
          y: yOffset,
        },
      });
    });

    yOffset += teamHeight;
  });

  return result;
}
